import React from "react";
import { ArrowDownTrayIcon, ArrowLeftIcon } from "@heroicons/react/24/solid";
import {
  Avatar,
  Button,
  Tooltip,
} from "@heroui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

import { asRecord } from "../../../utils/typeGuards";
import { DealAgreementResponseDto, DealResponseDto } from "../../../types/types";
import type { DealFormData } from "../createDeal/dealFormTypes";
import type { TranslationFn } from "../createDeal/dealFormFields";
import DealActivityTab from "./dealActivityTab";
import ComponentPaymentsTab from "./componentPaymentsTab";
import ResultRatingsTab from "./resultRatingTab";
import { BsDownload } from "react-icons/bs";
import { useNavigate } from "react-router-dom";

interface Props {
  deal: DealResponseDto | null;
  formData: DealFormData | null;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  t: TranslationFn;
  isFighter: boolean;
  canPayComponents: boolean;
  handlePayComponent: (id: number) => void;
  currentUserId: string;
  isManager: boolean;
  hasUserRated: boolean;
  refreshDeal: () => Promise<DealResponseDto | null>;
  downloadingInvoice: boolean;
  downloadingReceipt: boolean;
  downloadingAgreement: boolean;
  handleDownloadSummaryPdf: () => void;
  handleDownloadReceipt: () => void;
  handleDownloadAgreement: () => void;
  agreement?: DealAgreementResponseDto | null;
  onSignAgreement?: (signatureDataUrl: string) => Promise<void>;
  onAcceptDeal?: () => Promise<void>;
  onDeclineDeal?: () => Promise<void>;
  isDealActionProcessing?: boolean;
  uploadRequiredDocument?: (
    documentType: string,
    file: File,
  ) => Promise<void>;
  reviewRequiredDocument?: (
    documentType: string,
    status: "APPROVED" | "REJECTED",
    reviewComment?: string,
  ) => Promise<void>;
  renderDynamicObject: (obj: unknown, skip?: number) => React.ReactNode;
  renderSummary: (obj: unknown, maxFields?: number) => React.ReactNode;
  tr: (value?: string) => string;
}

const HIDDEN_FIELD_KEYS = new Set([
  "opponentFighterId",
  "opponent_fighter_id",
  "OpponentFighterId",
  "dealAccepter",
  "deal_accepter",
  "acceptedBy",
  "accepted_by",
  "dealAccepterId",
  "deal_accepter_id",
  "accepter",
  "accepterId",
]);

const SIDEBAR_SECTIONS = ["core", "details"];

const EVENT_RESPONSE_HIDDEN_KEYS = new Set([
  "matchupId",
  "matchup_id",
  "MatchupId",
]);

const EVENT_RESPONSE_FIELD_ORDER = [
  "eventName",
  "description",
  "opponent",
];

const EVENT_RESPONSE_LABELS: Record<string, string> = {
  eventName: "Event Name",
  matchupId: "Matchup ID",
  firstName: "First Name",
  lastName: "Last Name",
  profileType: "Profile Type",
  profileImage: "Profile Image",
};

const filterHiddenFields = (data: unknown): unknown => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;
  const record = data as Record<string, unknown>;
  return Object.fromEntries(
    Object.entries(record)
      .filter(([key]) => !HIDDEN_FIELD_KEYS.has(key))
      .map(([key, value]) => [key, filterHiddenFields(value)]),
  );
};

const isVisibleEventValue = (value: unknown): boolean => {
  if (value === null || value === undefined || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
};

const getEventResponseEntries = (data: unknown): Array<[string, unknown]> => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return [];

  const record = data as Record<string, unknown>;

  return Object.entries(record)
    .filter(
      ([key]) =>
        key !== "eventId" && !EVENT_RESPONSE_HIDDEN_KEYS.has(key),
    )
    .filter(([, value]) => isVisibleEventValue(value))
    .sort(([firstKey], [secondKey]) => {
      const firstIndex = EVENT_RESPONSE_FIELD_ORDER.indexOf(firstKey);
      const secondIndex = EVENT_RESPONSE_FIELD_ORDER.indexOf(secondKey);

      if (firstIndex === -1 && secondIndex === -1) return 0;
      if (firstIndex === -1) return 1;
      if (secondIndex === -1) return -1;
      return firstIndex - secondIndex;
    });
};

const formatEventResponseLabel = (key: string): string =>
  EVENT_RESPONSE_LABELS[key] ||
  String(key)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatEventResponseValue = (value: unknown): string => {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value.trim() || "-";
  return String(value || "-");
};

const formatTitleCase = (value: unknown): string =>
  formatEventResponseValue(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getOpponentProfileId = (opponent: Record<string, unknown>) => {
  const rawId = opponent.profileId || opponent.id || opponent.userId || null;
  return rawId as number | string | null;
};

const DealViewContent: React.FC<Props> = ({
  deal,
  formData,
  activeTab,
  setActiveTab,
  t,
  isFighter,
  canPayComponents,
  handlePayComponent,
  currentUserId,
  isManager,
  hasUserRated,
  refreshDeal,
  downloadingInvoice,
  downloadingReceipt,
  downloadingAgreement,
  handleDownloadSummaryPdf,
  handleDownloadReceipt,
  handleDownloadAgreement,
  agreement,
  onSignAgreement,
  onAcceptDeal,
  onDeclineDeal,
  isDealActionProcessing,
  uploadRequiredDocument,
  reviewRequiredDocument,
  renderDynamicObject,
  renderSummary,
  tr,
}) => {
  const showInvoiceTab = deal?.status === "FINISHED";
  const showResultTab = [
    "WAITING_FOR_RESULT",
    "RESULT_UPDATED",
    "RESULT_CONFIRMED",
    "FIGHTER_RATED",
    "MANAGER_RATED",
    "DISPUTE",
  ].includes(deal?.status || "");
  const currentUserRole: "MANAGER" | "FIGHTER" = isManager
    ? "MANAGER"
    : "FIGHTER";
  const navigate = useNavigate();
  const dealRecord = asRecord(deal);
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);

  const navigateToProfile = (profileId?: number | string | null) => {
    if (!profileId) return;
    navigate(`/profile?profileId=${profileId}`);
  };
  const tabs = [
    "general",
    "componentPayments",
    "progress",
    "agreement",
    "activity",
  ];

  React.useEffect(() => {
    if (activeTab === "result" && !showResultTab) {
      setActiveTab("progress");
    }
  }, [activeTab, setActiveTab, showInvoiceTab, showResultTab]);

  const sidebarFormData = (() => {
    if (!formData) return null;
    const entries = SIDEBAR_SECTIONS
      .filter((s) => formData[s] !== undefined)
      .map((s) => [s, filterHiddenFields(formData[s])]);
    return entries.length > 0 ? Object.fromEntries(entries) : null;
  })();

  const generalFormData = (() => {
    if (!formData) return null;
    const entries = Object.entries(formData)
      .filter(([key]) => !SIDEBAR_SECTIONS.includes(key))
      .map(([key, value]) => [key, filterHiddenFields(value)]);
    return entries.length > 0 ? Object.fromEntries(entries) : null;
  })();

  const eventResponseEntries = getEventResponseEntries(deal?.eventResponse);

  const renderEventResponseObject = (
    data: unknown,
    hiddenKeys: string[] = [],
  ): React.ReactNode => {
    const entries = getEventResponseEntries(data).filter(
      ([key]) => !hiddenKeys.includes(key),
    );

    if (entries.length === 0) {
      return <div className="text-sm text-gray-500">-</div>;
    }

    return (
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        {entries.map(([fieldKey, fieldValue]) => {
          const isObjectField =
            fieldValue &&
            typeof fieldValue === "object" &&
            !Array.isArray(fieldValue);

          if (fieldKey === "opponent" && isObjectField) {
            const opponent = fieldValue as Record<string, unknown>;
            const opponentName = [
              opponent.firstName,
              opponent.lastName,
            ]
              .filter(Boolean)
              .join(" ")
              .trim();
            const opponentProfileId = getOpponentProfileId(opponent);

            return (
              <div key={fieldKey} className="col-span-2 min-w-0">
                <div className="text-sm text-gray-500 leading-5">
                  {t("opponent")}
                </div>
                <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <button
                    type="button"
                    disabled={!opponentProfileId}
                    onClick={() => navigateToProfile(opponentProfileId)}
                    className={`flex min-w-0 items-center gap-3 rounded-md text-left ${
                      opponentProfileId
                        ? "cursor-pointer transition hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                        : "cursor-default"
                    }`}
                  >
                    {opponent.profileImage ? (
                      <Avatar size="sm" src={String(opponent.profileImage)} />
                    ) : null}
                    <div className="min-w-0">
                      <div className="break-words text-sm font-semibold text-gray-900">
                        {opponentName || "-"}
                      </div>
                      {opponent.profileType ? (
                        <div className="text-sm text-gray-500 leading-5">
                          {formatTitleCase(opponent.profileType)}
                        </div>
                      ) : null}
                    </div>
                  </button>
                </div>
              </div>
            );
          }

          if (isObjectField) {
            return (
              <div key={fieldKey} className="col-span-2 min-w-0">
                <div className="text-sm text-gray-500 leading-5">
                  {formatEventResponseLabel(fieldKey)}
                </div>
                <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  {renderEventResponseObject(fieldValue)}
                </div>
              </div>
            );
          }

          if (Array.isArray(fieldValue)) {
            return (
              <div key={fieldKey} className="col-span-2 min-w-0">
                <div className="text-sm text-gray-500 leading-5">
                  {formatEventResponseLabel(fieldKey)}
                </div>
                <div className="mt-1 text-sm font-semibold text-gray-900 break-words leading-5">
                  {fieldValue.map(formatEventResponseValue).join(", ")}
                </div>
              </div>
            );
          }

          return (
            <div key={fieldKey} className="min-w-0">
              <div className="text-sm text-gray-500 leading-5">
                {formatEventResponseLabel(fieldKey)}
              </div>
              <div className="mt-1 text-sm font-semibold text-gray-900 break-words leading-5">
                {formatEventResponseValue(fieldValue)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const allTabs = [
    ...tabs,
    ...(showResultTab ? ["result"] : []),
    ...(deal?.ratingResponse && deal.ratingResponse.length > 0 ? ["ratings"] : []),
  ];

  const tabLabel = (tabKey: string): string => {
    if (tabKey === "agreement")
      return t("agreementTabLabel");
    const raw = t(tabKey);
    if (raw && raw !== tabKey) return raw;
    const map: Record<string, string> = {
      general: "General",
      componentPayments: "Component Payments",
      progress: "Progress",
      activity: "Activity",
      invoice: "Invoice",
      result: "Result",
      ratings: "Ratings",
    };
    return map[tabKey] || tabKey;
  };

  const sidebarContent = (
    <>
      <div className="flex flex-col gap-2">
        <div className="border-b border-gray-200 pb-1">
          <span className="text-base font-semibold text-gray-500 font-['Plus_Jakarta_Sans'] leading-6">
            {t("dealAccepter")}
          </span>
        </div>
        <div
          className="flex items-center justify-between gap-3 cursor-pointer hover:bg-gray-50 p-1.5 rounded-2xl transition"
          onClick={() => navigateToProfile(deal?.acceptedBy?.id)}
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Avatar size="sm" className="w-9 h-9" src={deal?.acceptedBy?.profileImage} />
            <div className="min-w-0">
              <p className="font-medium text-gray-900 text-sm font-['Plus_Jakarta_Sans'] leading-5 truncate">
                {deal?.acceptedBy?.firstName} {deal?.acceptedBy?.lastName}
              </p>
            </div>
          </div>
          <ChevronDownIcon
            className={`w-5 h-5 text-gray-400 shrink-0 transition-transform lg:hidden ${mobileSidebarOpen ? "rotate-180" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setMobileSidebarOpen((prev) => !prev);
            }}
          />
        </div>
      </div>

      <div className={`lg:block ${mobileSidebarOpen ? "block" : "hidden"} mt-4 flex flex-col gap-6`}>
        {deal?.agreementFile?.fileUrl && (
          <div className="mb-6">
            <Tooltip content={t("downloadAgreement") || "Download agreement"} placement="bottom">
              <Button
                size="sm"
                color="primary"
                variant="flat"
                isLoading={downloadingAgreement}
                onClick={handleDownloadAgreement}
                className="w-full flex items-center gap-2 justify-center"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                {t("downloadAgreement") || "Download Agreement"}
              </Button>
            </Tooltip>
          </div>
        )}

        {eventResponseEntries.length > 0 && (
          <div className="mb-6">
            <div className="border-b border-gray-200 pb-1 mb-3">
              <span className="text-base font-semibold text-gray-500 font-['Plus_Jakarta_Sans'] leading-6">
                {t("eventDetails")}
              </span>
            </div>
            {renderEventResponseObject(deal?.eventResponse)}
          </div>
        )}

        {sidebarFormData ? (
          <div>
            {renderSummary(sidebarFormData)}
          </div>
        ) : (
          <div className="text-sm text-gray-500">{t("noFormData")}</div>
        )}
      </div>
    </>
  );

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between sticky top-0 z-40 shadow-sm">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button
            type="button"
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate("/mydeals");
              }
            }}
            className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 transition shrink-0"
            aria-label="Go back"
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </button>
          <h1 className="text-base sm:text-xl font-bold text-gray-900 tracking-tight truncate">
            {String(dealRecord.dealName || dealRecord.title || (deal?.dealType ? formatTitleCase(deal?.dealType) : "Fight single"))} document
          </h1>
        </div>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
            {tabs.map((tabKey) => (
              <button
                key={tabKey}
                onClick={() => setActiveTab(tabKey)}
                className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all duration-150 ${
                  activeTab === tabKey
                    ? "bg-white text-gray-900 shadow-sm font-semibold"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tabLabel(tabKey)}
              </button>
            ))}

            {showResultTab && (
              <button
                onClick={() => setActiveTab("result")}
                className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all duration-150 ${
                  activeTab === "result"
                    ? "bg-white text-gray-900 shadow-sm font-semibold"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tabLabel("result")}
              </button>
            )}

            {deal?.ratingResponse && deal.ratingResponse.length > 0 && (
              <button
                onClick={() => setActiveTab("ratings")}
                className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all duration-150 ${
                  activeTab === "ratings"
                    ? "bg-white text-gray-900 shadow-sm font-semibold"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tabLabel("ratings")}
              </button>
            )}
          </div>

          <Tooltip content={t("dealSummary")} placement="bottom">
            <Button
              size="sm"
              variant="flat"
              isLoading={downloadingInvoice}
              onClick={handleDownloadSummaryPdf}
              className="flex items-center gap-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-full px-4 py-2 font-semibold text-xs sm:text-sm border border-blue-200/50"
            >
              <span>
                {t("downloadDocument")}
              </span>
              {!downloadingInvoice && <ArrowDownTrayIcon className="w-4 h-4" />}
            </Button>
          </Tooltip>
          {showInvoiceTab && (
            <Tooltip content={t("downloadReceipt")} placement="bottom">
              <Button
                size="sm"
                variant="flat"
                isLoading={downloadingReceipt}
                onClick={handleDownloadReceipt}
                className="flex items-center gap-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-full px-4 py-2 font-semibold text-xs sm:text-sm border border-blue-200/50"
              >
                <span>Download Invoice</span>
                {!downloadingReceipt && <ArrowDownTrayIcon className="w-4 h-4" />}
              </Button>
            </Tooltip>
          )}
        </div>

        <div className="lg:hidden flex items-center gap-2 shrink-0 ml-2">
          <button
            type="button"
            onClick={handleDownloadSummaryPdf}
            disabled={downloadingInvoice}
            className="flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200/50 rounded-full px-3 py-1.5 text-xs font-semibold"
          >
            {downloadingInvoice ? (
              <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <ArrowDownTrayIcon className="w-3.5 h-3.5" />
            )}
            <span className="hidden xs:inline">Download</span>
          </button>
        </div>
      </div>

      <div className="lg:hidden bg-white border-b border-gray-200 sticky top-14 z-30">
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex items-center gap-1.5 px-3 py-2 min-w-max">
            {allTabs.map((tabKey) => (
              <button
                key={tabKey}
                onClick={() => setActiveTab(tabKey)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all duration-150 ${
                  activeTab === tabKey
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-gray-600 bg-gray-100 hover:bg-gray-200"
                }`}
              >
                {tabLabel(tabKey)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-6 max-w-full p-6 items-start hidden lg:flex">
        <div className="w-[400px] lg:w-[480px] shrink-0 bg-white rounded-3xl p-6 shadow-sm border border-gray-100 self-start text-left">
          {sidebarContent}
        </div>

        <div className="flex-1 min-w-0 text-left">
          <div className="min-h-[600px]">
            {["general", "progress", "activity", "agreement"].includes(activeTab) && (
              <DealActivityTab
                deal={deal!}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                t={t}
                formData={generalFormData}
                renderDynamicObject={renderDynamicObject}
                currentUserRole={currentUserRole}
                agreement={agreement}
                onSignAgreement={onSignAgreement}
                onAcceptDeal={onAcceptDeal}
                onDeclineDeal={onDeclineDeal}
                isDealActionProcessing={isDealActionProcessing}
                onUploadRequiredDocument={uploadRequiredDocument}
                onReviewRequiredDocument={reviewRequiredDocument}
                tr={tr}
              />
            )}

            {activeTab === "componentPayments" && (
              <ComponentPaymentsTab
                key={JSON.stringify(
                  deal?.componentPayments?.map((p) => p.status),
                )}
                deal={deal!}
                formData={formData}
                isFighter={isFighter}
                canPayComponents={canPayComponents}
                handlePayComponent={handlePayComponent}
                t={t}
              />
            )}

            {((activeTab === "result" && showResultTab) ||
              activeTab === "ratings") && (
              <ResultRatingsTab
                deal={deal!}
                activeTab={activeTab}
                currentUserId={currentUserId || ""}
                isManager={isManager}
                hasUserRated={hasUserRated}
                refreshDeal={refreshDeal}
                tr={tr}
              />
            )}

            {activeTab === "invoice" && showInvoiceTab && (
              <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 flex min-h-[160px] items-center justify-center">
                <Button
                  isLoading={downloadingReceipt}
                  onClick={handleDownloadReceipt}
                  size="md"
                  className="flex items-center justify-center gap-2 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded-xl px-4 py-2 shadow-sm font-medium"
                >
                  {!downloadingReceipt && <BsDownload size={16} />}
                  <span>{t("downloadReceipt")}</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="lg:hidden flex flex-col gap-0 px-3 sm:px-4 pt-3 sm:pt-4 pb-10">
        <div className="bg-white rounded-2xl sm:rounded-3xl p-4 shadow-sm border border-gray-100 text-left mb-4">
          {sidebarContent}
        </div>

        <div className="min-h-[400px] text-left">
          {["general", "progress", "activity", "agreement"].includes(activeTab) && (
            <DealActivityTab
              deal={deal!}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              t={t}
              formData={generalFormData}
              renderDynamicObject={renderDynamicObject}
              currentUserRole={currentUserRole}
              agreement={agreement}
              onSignAgreement={onSignAgreement}
              onAcceptDeal={onAcceptDeal}
              onDeclineDeal={onDeclineDeal}
              isDealActionProcessing={isDealActionProcessing}
              onUploadRequiredDocument={uploadRequiredDocument}
              onReviewRequiredDocument={reviewRequiredDocument}
              tr={tr}
            />
          )}

          {activeTab === "componentPayments" && (
            <ComponentPaymentsTab
              key={JSON.stringify(
                deal?.componentPayments?.map((p) => p.status),
              )}
              deal={deal!}
              formData={formData}
              isFighter={isFighter}
              canPayComponents={canPayComponents}
              handlePayComponent={handlePayComponent}
              t={t}
            />
          )}

          {((activeTab === "result" && showResultTab) ||
            activeTab === "ratings") && (
            <ResultRatingsTab
              deal={deal!}
              activeTab={activeTab}
              currentUserId={currentUserId || ""}
              isManager={isManager}
              hasUserRated={hasUserRated}
              refreshDeal={refreshDeal}
              tr={tr}
            />
          )}

          {activeTab === "invoice" && showInvoiceTab && (
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 flex min-h-[160px] items-center justify-center">
              <Button
                isLoading={downloadingReceipt}
                onClick={handleDownloadReceipt}
                size="md"
                className="flex items-center justify-center gap-2 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded-xl px-4 py-2 shadow-sm font-medium"
              >
                {!downloadingReceipt && <BsDownload size={16} />}
                <span>{t("downloadReceipt")}</span>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealViewContent; 
